import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Shield, Upload, CheckCircle, XCircle, AlertTriangle, Clock } from 'lucide-react';

type VerificationStatus = 'unverified' | 'pending' | 'verified' | 'rejected';

interface VerificationDocument {
  id: string;
  type: 'government_id' | 'passport' | 'driver_license' | 'business_registration';
  name: string;
  uploadedAt: Date;
  status: VerificationStatus;
  rejectionReason?: string;
}

// Mock verification data
const mockVerificationData = {
  status: 'unverified' as VerificationStatus,
  documents: [] as VerificationDocument[],
  verifiedAt: null as Date | null,
  verificationBadge: false
};

export default function VerificationSettings() {
  const { theme } = useTheme();
  const [verificationData, setVerificationData] = useState(mockVerificationData);
  const [selectedDocType, setSelectedDocType] = useState<VerificationDocument['type']>('government_id');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const documentTypes = [
    { value: 'government_id', label: 'Government ID' },
    { value: 'passport', label: 'Passport' },
    { value: 'driver_license', label: "Driver's License" },
    { value: 'business_registration', label: 'Business Registration' }
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type and size
      if (!selectedFile.type.startsWith('image/') && selectedFile.type !== 'application/pdf') {
        setError('Please upload an image or PDF file');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    // Simulate document upload and verification process
    setVerificationData(prev => ({
      ...prev,
      status: 'pending',
      documents: [
        ...prev.documents,
        {
          id: Date.now().toString(),
          type: selectedDocType,
          name: file.name,
          uploadedAt: new Date(),
          status: 'pending'
        }
      ]
    }));

    // Reset form
    setFile(null);
    const fileInput = document.getElementById('document-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';

    // Simulate verification process
    setTimeout(() => {
      setVerificationData(prev => ({
        ...prev,
        status: 'verified',
        verifiedAt: new Date(),
        verificationBadge: true,
        documents: prev.documents.map(doc => ({
          ...doc,
          status: 'verified'
        }))
      }));
    }, 3000);
  };

  const getStatusColor = (status: VerificationStatus) => {
    switch (status) {
      case 'verified':
        return theme === 'dark' ? 'text-green-400' : 'text-green-600';
      case 'pending':
        return theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600';
      case 'rejected':
        return theme === 'dark' ? 'text-red-400' : 'text-red-600';
      default:
        return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const getStatusIcon = (status: VerificationStatus) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Verification Status */}
      <div className={`
        p-6 rounded-lg border
        ${theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
        }
      `}>
        <div className="flex items-center space-x-4 mb-6">
          {getStatusIcon(verificationData.status)}
          <div>
            <h3 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Verification Status: {verificationData.status.charAt(0).toUpperCase() + verificationData.status.slice(1)}
            </h3>
            <p className={`text-sm ${getStatusColor(verificationData.status)}`}>
              {verificationData.status === 'verified'
                ? `Verified on ${verificationData.verifiedAt?.toLocaleDateString()}`
                : verificationData.status === 'pending'
                ? 'Your documents are being reviewed'
                : 'Submit your documents for verification'}
            </p>
          </div>
        </div>

        {verificationData.verificationBadge && (
          <div className={`
            flex items-center space-x-2 p-3 rounded-lg
            ${theme === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}
          `}>
            <Shield className="h-5 w-5 text-green-500" />
            <span className={`text-sm font-medium ${
              theme === 'dark' ? 'text-green-400' : 'text-green-600'
            }`}>
              Your account is now verified with a blue checkmark
            </span>
          </div>
        )}
      </div>

      {/* Document Upload */}
      {verificationData.status !== 'verified' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Document Type
            </label>
            <select
              value={selectedDocType}
              onChange={(e) => setSelectedDocType(e.target.value as VerificationDocument['type'])}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            >
              {documentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Upload Document
            </label>
            <div className={`
              border-2 border-dashed rounded-lg p-6 text-center
              ${theme === 'dark'
                ? 'border-gray-600 hover:border-gray-500'
                : 'border-gray-300 hover:border-gray-400'
              }
              ${error ? 'border-red-500' : ''}
            `}>
              <input
                type="file"
                id="document-upload"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="document-upload"
                className="cursor-pointer"
              >
                <Upload className={`h-8 w-8 mx-auto mb-2 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                  {file
                    ? file.name
                    : 'Click to upload or drag and drop'}
                </p>
                <p className={`text-sm mt-1 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  PDF or images up to 5MB
                </p>
              </label>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-500">
                {error}
              </p>
            )}
          </div>

          <div className={`
            p-4 rounded-lg
            ${theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'}
          `}>
            <h4 className={`text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Requirements:
            </h4>
            <ul className={`text-sm space-y-1 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <li>• Document must be valid and not expired</li>
              <li>• All information must be clearly visible</li>
              <li>• File must be less than 5MB</li>
              <li>• Supported formats: JPG, PNG, PDF</li>
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!file}
              className={`
                px-6 py-2 rounded-lg transition-colors
                ${file
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              Submit for Verification
            </button>
          </div>
        </form>
      )}

      {/* Document History */}
      {verificationData.documents.length > 0 && (
        <div>
          <h3 className={`text-lg font-medium mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Document History
          </h3>
          <div className="space-y-4">
            {verificationData.documents.map(doc => (
              <div
                key={doc.id}
                className={`
                  p-4 rounded-lg
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}
                `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {documentTypes.find(t => t.value === doc.type)?.label}
                    </p>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Uploaded on {doc.uploadedAt.toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(doc.status)}
                    <span className={`text-sm ${getStatusColor(doc.status)}`}>
                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                    </span>
                  </div>
                </div>
                {doc.rejectionReason && (
                  <p className={`mt-2 text-sm ${
                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                  }`}>
                    Reason: {doc.rejectionReason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}